import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { useToast } from '@/shared/hooks';
import { templateMetadataSchema } from '../schemas';
import type { TemplateMetadataFormValues } from '../schemas';
import { useCreateProjectTemplate } from '../hooks/useProjectTemplateMutations';
import { TEMPLATE_SECTOR } from '../types';
import type {
  TemplateCustomField,
  TemplateGroup,
  TemplateStatus,
  TemplateTag,
  TemplateView,
} from '../types';
import { TemplateEditorSections } from './TemplateEditorSections';

interface TemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (templateId: string) => void;
}

/** "New template from scratch" — creation only. Editing an existing tenant template's structure happens inline in `TemplatePreviewPanel`. */
export function TemplateEditorDialog({ open, onOpenChange, onCreated }: TemplateEditorDialogProps) {
  const { t } = useTranslation('projectTemplates');
  const { toast } = useToast();
  const createTemplate = useCreateProjectTemplate();

  const [groups, setGroups] = useState<TemplateGroup[]>([]);
  const [statuses, setStatuses] = useState<TemplateStatus[]>([]);
  const [views, setViews] = useState<TemplateView[]>([]);
  const [customFields, setCustomFields] = useState<TemplateCustomField[]>([]);
  const [tags, setTags] = useState<TemplateTag[]>([]);

  const { control, handleSubmit, reset } = useForm<TemplateMetadataFormValues>({
    resolver: zodResolver(templateMetadataSchema),
    defaultValues: { title: '', sector: TEMPLATE_SECTOR.Technology, description: '' },
  });

  const sectorOptions = Object.values(TEMPLATE_SECTOR).map((sector) => ({
    value: sector,
    label: t(`gallery.sectorFilter.${sector}`),
  }));

  function resetDraft() {
    reset({ title: '', sector: TEMPLATE_SECTOR.Technology, description: '' });
    setGroups([]);
    setStatuses([]);
    setViews([]);
    setCustomFields([]);
    setTags([]);
  }

  function handleOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) resetDraft();
  }

  function onSubmit(values: TemplateMetadataFormValues) {
    createTemplate.mutate(
      {
        ...values,
        boards: [
          {
            name: 'Main board',
            description: null,
            order: 0,
            statuses,
            views,
            groups,
            ungroupedTasks: [],
          },
        ],
        customFields,
        tags,
      },
      {
        onSuccess: (res) => {
          toast.success({ title: t('toast.created') });
          if (res.data.id) onCreated(res.data.id);
          handleOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none overflow-y-auto md:h-auto md:max-h-[85vh] md:max-w-2xl md:rounded-lg"
        data-testid="project-templates-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('editor.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('editor.title')}</DialogDescription>
        </DialogHeader>

        <form
          id="project-template-editor-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <FormFieldInput
            control={control}
            name="title"
            label={t('editor.fields.name')}
            floatingLabel
            disabled={createTemplate.isPending}
            data-testid="project-templates-editor-name-input"
          />
          <FormFieldSelect
            control={control}
            name="sector"
            label={t('editor.fields.sector')}
            floatingLabel
            options={sectorOptions}
            disabled={createTemplate.isPending}
            data-testid="project-templates-editor-sector-select"
          />
          <FormFieldTextarea
            control={control}
            name="description"
            label={t('editor.fields.description')}
            floatingLabel
            disabled={createTemplate.isPending}
            data-testid="project-templates-editor-description-input"
          />
        </form>

        <TemplateEditorSections
          groups={groups}
          statuses={statuses}
          views={views}
          customFields={customFields}
          tags={tags}
          setGroups={setGroups}
          setStatuses={setStatuses}
          setViews={setViews}
          setCustomFields={setCustomFields}
          setTags={setTags}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createTemplate.isPending}
          >
            {t('editor.cancel')}
          </Button>
          <Button
            type="submit"
            form="project-template-editor-form"
            disabled={createTemplate.isPending}
            data-testid="project-templates-editor-save-button"
          >
            {createTemplate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('editor.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
